/**
 * fanola.hair "Added to basket" toast (spec §10) - replaces the auto-opening cart drawer.
 *
 * Ported from HDS shopify/snippets/cart-notification.liquid, minus everything HDS-specific:
 * B2B price settle, sold-out-shade substitute banner, bulk-shade button, recommendations.
 * Fanola has no trade tiers, so the add response's own line price is the price.
 *
 * Satisfies the hook ProductForm has always had (assets/theme.js:2144):
 *     document.querySelector('cart-notification') || document.querySelector('cart-drawer')
 * complements.js and product-group.js use the same lookup. The drawer is still re-rendered
 * on every add (getSectionsToRender) - we just never open it. Markup + CSS:
 * snippets/cart-notification.liquid. Rollback: remove its render from layout/theme.liquid.
 */
(function () {
  'use strict';

  var DISMISS_MS = 10000;

  function thumbUrl(url) {
    if (!url) return '';
    var q = url.indexOf('?');
    var base = q === -1 ? url : url.slice(0, q);
    var qs = q === -1 ? '' : url.slice(q);
    var dot = base.lastIndexOf('.');
    if (dot === -1) return url;
    return base.slice(0, dot) + '_160x' + base.slice(dot) + qs;
  }

  /** The line to show. An items-array add (add-both, bundles) shows its LAST item. */
  function lineOf(parsed) {
    if (parsed && parsed.items && parsed.items.length) return parsed.items[parsed.items.length - 1];
    return parsed || null;
  }

  function summarize(parsed) {
    var item = lineOf(parsed) || {};
    var values = (item.options_with_values || [])
      .map(function (o) { return o && o.value; })
      .filter(function (v) { return v && v !== 'Default Title'; });
    return {
      item: item,
      title: item.product_title || '',
      variantLabel: values.join(' / '),
      quantity: item.quantity || 0,
      image: thumbUrl(item.image),
      imageRaw: item.image || '',
      url: item.url || '',
      linePrice: typeof item.final_line_price === 'number' ? item.final_line_price : null
    };
  }

  /** Line prices come back in the shopper's presentment currency (Markets), so format with its code. */
  function money(pence, currency) {
    var amount = Number(pence) / 100;
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
    } catch (e) {
      return '£' + amount.toFixed(2);
    }
  }

  function panelTop(headerBottom) {
    var GAP = 12;
    if (typeof headerBottom !== 'number') return GAP;
    return Math.max(GAP, headerBottom + GAP);
  }

  /** The toast is being read by pointer hover or by KEYBOARD focus (mouse-click focus does not count). */
  function isReading(state) {
    return !!state && (!!state.hovering || !!state.keyboardFocused);
  }

  function shouldRunDismissTimer(state) {
    return !!state && !state.interacting && !state.held;
  }

  /** The drawer's own checkout button: clicking it runs the theme's normal checkout path. */
  function checkoutTarget(doc) {
    return doc.getElementById('CartDrawer-Checkout') || null;
  }

  window.CartNotificationHelpers = { lineOf: lineOf, summarize: summarize, money: money, panelTop: panelTop,
    shouldRunDismissTimer: shouldRunDismissTimer, isReading: isReading, checkoutTarget: checkoutTarget, thumbUrl: thumbUrl,
    DISMISS_MS: DISMISS_MS };
  if (typeof customElements === 'undefined' || customElements.get('cart-notification')) return;

  customElements.define('cart-notification', class CartNotification extends HTMLElement {
    constructor() {
      super();
      this.interacting = false;
      this.hovering = false;
      this.keyboardFocused = false;
      this.held = false;
      this.dismissTimer = null;
      this.hideTimer = null;
      this.repositionFrame = null;
      this.querySelector('[data-cn-close]').addEventListener('click', this.close.bind(this));
      this.querySelector('[data-cn-checkout]').addEventListener('click', this.onCheckout.bind(this));
      this.querySelector('[data-cn-view]').addEventListener('click', this.onView.bind(this));
      // Reading the toast holds it open: pointer hover, or KEYBOARD focus only. Mouse-driven focus
      // (e.g. the developer pop-out restoring focus to its button) must not pin the toast open.
      var refresh = function () {
        this.interacting = isReading({ hovering: this.hovering, keyboardFocused: this.keyboardFocused });
        this.syncDismissTimer();
      }.bind(this);
      this.addEventListener('mouseenter', function () { this.hovering = true; refresh(); }.bind(this));
      this.addEventListener('mouseleave', function () { this.hovering = false; refresh(); }.bind(this));
      this.addEventListener('focusin', function (e) {
        var kb = true;   // engines without :focus-visible: treat as keyboard focus
        try { kb = !!(e.target && e.target.matches && e.target.matches(':focus-visible')); } catch (err) { kb = true; }
        this.keyboardFocused = kb;
        refresh();
      }.bind(this));
      this.addEventListener('focusout', function () { this.keyboardFocused = false; refresh(); }.bind(this));
      this.onKeydown = function (e) {
        if (e.key !== 'Escape') return;
        // Escape belongs to the drawer / developer pop-out when either is up.
        if (document.querySelector('cart-drawer.active')) return;
        var pop = document.getElementById('dev-popout');
        if (pop && !pop.hidden) return;
        this.close();
      }.bind(this);
      this.onRepositionBound = this.onReposition.bind(this);
      // The header cart icons (`.mbCart` mobile, `#cartLink` desktop) open the drawer, so
      // nothing else would dismiss us over it.
      document.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.mbCart, #cartLink') && !this.hidden) this.hide();
      }.bind(this), true);
      // Space on #cartLink opens the drawer from a keydown handler (theme.js), not a click.
      document.addEventListener('keydown', function (e) {
        if (e.key !== ' ' && e.code !== 'Space') return;
        if (e.target && e.target.closest && e.target.closest('#cartLink') && !this.hidden) this.hide();
      }.bind(this), true);
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
    }

    setActiveElement(el) { this.activeElement = el; }

    applyDrawerHTML(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('#CartDrawer');
      var dest = document.querySelector('#CartDrawer');
      if (fresh && dest) dest.innerHTML = fresh.innerHTML;
      var fw = doc.querySelector('cart-drawer');
      var dw = document.querySelector('cart-drawer');
      if (fw && dw) dw.classList.toggle('is-empty', fw.classList.contains('is-empty'));
    }

    renderContents(parsed) {
      // An add from inside an open drawer (e.g. gift wrap) belongs to the drawer, not a toast behind it.
      var dw = document.querySelector('cart-drawer.active');
      if (dw && typeof dw.renderContents === 'function') return dw.renderContents(parsed);
      var sections = parsed && parsed.sections;
      // 1. Keep the drawer (and its upsell card) current in the background. No open().
      if (sections && sections['cart-drawer']) {
        try { this.applyDrawerHTML(sections['cart-drawer']); } catch (e) {}
      }
      // 2. Unhide BEFORE writing the text: a hidden live region's changes are not announced.
      this.summary = summarize(parsed);
      this.open();
      this.paint(this.summary);
      if (typeof window.freeShippMsg === 'function') { try { window.freeShippMsg(); } catch (e) {} }
      // assets/cart-dev-popout.js decides whether this add earns a Developers button.
      document.dispatchEvent(new CustomEvent('cart-notification:open', { detail: { item: this.summary.item } }));
    }

    paint(s) {
      var img = this.querySelector('[data-cn-image]');
      if (s.image) {
        img.onerror = function () {
          img.onerror = null;
          if (s.imageRaw && img.getAttribute('src') !== s.imageRaw) img.src = s.imageRaw;
          else img.hidden = true;
        };
        img.hidden = false;
        img.src = s.image;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
      this.querySelector('[data-cn-title]').textContent = s.title;
      var variant = this.querySelector('[data-cn-variant]');
      variant.textContent = s.variantLabel;
      variant.hidden = !s.variantLabel;
      this.querySelector('[data-cn-qty]').textContent = 'Qty ' + s.quantity;
      this.querySelector('[data-cn-price]').textContent = s.linePrice === null ? '' : money(s.linePrice, (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP');
      Array.prototype.forEach.call(this.querySelectorAll('[data-cn-link]'), function (a) {
        if (s.url) a.href = s.url; else a.removeAttribute('href');
      });
      // The drawer was just re-rendered from this add's own sections, so its subtotal is current.
      var sub = document.querySelector('#CartDrawer .cart_total b:last-child');
      var row = this.querySelector('.cn__sub');
      var text = sub ? sub.textContent.trim() : '';
      this.querySelector('[data-cn-subtotal]').textContent = text;
      if (row) row.hidden = !text;
    }

    /** The developer pop-out holds the toast open while it is up. */
    hold(on) {
      this.held = !!on;
      this.syncDismissTimer();
    }

    syncDismissTimer() {
      clearTimeout(this.dismissTimer);
      if (this.hidden) return;
      if (!shouldRunDismissTimer({ interacting: this.interacting, held: this.held })) return;
      this.dismissTimer = setTimeout(this.autoDismiss.bind(this), DISMISS_MS);
    }

    onCheckout(e) {
      if (e) e.preventDefault();
      var t = checkoutTarget(document);
      if (t && !t.disabled) { t.click(); return; }
      // No usable checkout control (e.g. a terms checkbox gates it): show the basket instead.
      this.onView();
    }

    onView(e) {
      if (e) e.preventDefault();
      this.hide();
      var d = document.querySelector('cart-drawer');
      if (d && typeof d.open === 'function') d.open();
      else window.location = (window.routes && window.routes.cart_url) || '/cart';
    }

    position() {
      var header = document.querySelector('#header');
      var bottom = header ? header.getBoundingClientRect().bottom : null;
      this.style.setProperty('--cn-top', panelTop(bottom) + 'px');
    }

    onReposition() {
      if (this.repositionFrame) return;
      this.repositionFrame = requestAnimationFrame(function () {
        this.repositionFrame = null;
        this.position();
      }.bind(this));
    }

    open() {
      clearTimeout(this.dismissTimer);
      clearTimeout(this.hideTimer);   // a second add mid-fade must cancel the pending hide
      this.hidden = false;
      this.classList.remove('cn--out');
      this.classList.add('cn--in');
      // The collection quick-add sets body.loading and relies on CartDrawer.open() to clear it.
      document.body.classList.remove('loading');
      this.position();
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onRepositionBound, { passive: true });
      window.addEventListener('resize', this.onRepositionBound, { passive: true });
      this.syncDismissTimer();
      // Deliberately NOT modal: no overflow-hidden, no focus trap.
    }

    hide() {
      clearTimeout(this.dismissTimer);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onRepositionBound);
      window.removeEventListener('resize', this.onRepositionBound);
      if (this.repositionFrame) { cancelAnimationFrame(this.repositionFrame); this.repositionFrame = null; }
      this.classList.remove('cn--in');
      this.classList.add('cn--out');
      var self = this;
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(function () { self.hidden = true; self.classList.remove('cn--out'); }, 200);
    }

    /** User-initiated (×, Escape): focus goes back to where they were. */
    close() {
      var hadFocus = this.contains(document.activeElement);
      this.hide();
      if (hadFocus && this.activeElement && document.contains(this.activeElement)) {
        try { this.activeElement.focus({ preventScroll: true }); } catch (e) {}
      }
    }

    /** Timer-initiated: never moves focus - the shopper has moved on. */
    autoDismiss() { this.hide(); }
  });
})();
